import React from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';

import {
  AvailableViewSummary,
  formatThresholdValuesHeading,
  resolveViewLegendItems,
} from 'domain/surveyDataManager/contracts/availableViews';
import { RecipeComponentFailure } from 'domain/surveyDataManager/contracts/recipes';

export interface AvailableViewItemProps {
  readonly view: AvailableViewSummary;
  readonly isVisible: boolean;
  readonly isExpanded: boolean;
  readonly isActivating: boolean;
  readonly isCatalogueLoading?: boolean;
  readonly activationFailures: readonly RecipeComponentFailure[];
  readonly isFavourite: boolean;
  readonly onToggleVisibility: (viewId: string) => void;
  readonly onToggleDetails: (viewId: string) => void;
  readonly onToggleFavourite: (view: AvailableViewSummary) => void;
}

function failureReasonLabel(reason: RecipeComponentFailure['reason']): string {
  switch (reason) {
    case 'defect_not_found':
    case 'measurement_not_found':
      return 'not in catalogue';
    case 'survey_year_not_found':
      return 'no survey year';
    case 'lane_not_found':
      return 'no lane';
    case 'already_active':
      return 'already on map';
    default: {
      const unreachable: never = reason;
      return unreachable;
    }
  }
}

function AvailableViewItem({
  view,
  isVisible,
  isExpanded,
  isActivating,
  isCatalogueLoading = false,
  activationFailures,
  isFavourite,
  onToggleVisibility,
  onToggleDetails,
  onToggleFavourite,
}: AvailableViewItemProps): JSX.Element {
  const showDisabled = isCatalogueLoading;
  const legendItems = resolveViewLegendItems(view);
  const thresholdHeading = formatThresholdValuesHeading(view);
  return (
    <li
      className={`sdm-available-views__item${isExpanded ? ' sdm-available-views__item--expanded' : ''}${
        isVisible ? ' sdm-available-views__item--visible' : ''
      }`}
      aria-current={isVisible ? 'true' : undefined}
    >
      <div className="sdm-available-views__item-row">
        <div className="sdm-available-views__item-info">
          <div className="sdm-available-views__item-title-row">
            <span className="sdm-available-views__item-name">{view.name}</span>
            {!view.editable ? (
              <span className="sdm-available-views__badge sdm-available-views__badge--readonly">
                Read-only
              </span>
            ) : null}
          </div>
          <span className="sdm-available-views__item-meta">
            {view.componentCount} parameter
            {view.componentCount === 1 ? '' : 's'}
            {view.componentCount > 1
              ? ' · one control shows or hides all together'
              : ''}
          </span>
          {/* Legend preview strip hidden for now
          {legendItems.length > 0 ? (
            <div
              className="sdm-available-views__legend"
              aria-hidden="true"
              title="Legend preview"
            >
              {legendItems.map((item) => (
                <span
                  key={`${view.id}-${item.color}-${item.label}`}
                  className="sdm-available-views__legend-swatch"
                  style={{ backgroundColor: item.color }}
                />
              ))}
            </div>
          ) : null}
          */}
        </div>
        <div className="sdm-available-views__item-actions">
          <button
            type="button"
            className="sdm-available-views__favourite-button"
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            aria-label={
              isFavourite
                ? `Remove ${view.name} from favourites`
                : `Add ${view.name} to favourites`
            }
            aria-pressed={isFavourite}
            onClick={() => onToggleFavourite(view)}
          >
            {isFavourite ? (
              <AiFillStar color="#FFC845" size={18} aria-hidden />
            ) : (
              <AiOutlineStar size={18} aria-hidden />
            )}
          </button>
          <button
            type="button"
            className="sdm-available-views__icon-button"
            onClick={() => onToggleDetails(view.id)}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? `Hide details for ${view.name}`
                : `Show details for ${view.name}`
            }
          >
            {isExpanded ? 'Hide details' : 'Details'}
          </button>
          <button
            type="button"
            className="sdm-available-views__icon-button"
            onClick={() => onToggleVisibility(view.id)}
            aria-pressed={isVisible}
            aria-busy={isActivating || isCatalogueLoading || undefined}
            disabled={showDisabled}
            aria-label={isVisible ? `Hide ${view.name}` : `See ${view.name} in map`}
          >
            {isActivating || isCatalogueLoading
              ? 'Loading…'
              : isVisible
                ? 'Hide'
                : 'See in Map'}
          </button>
        </div>
      </div>

      <div
        className={`sdm-available-views__details-wrap${
          isExpanded ? ' sdm-available-views__details-wrap--open' : ''
        }`}
        aria-hidden={!isExpanded}
      >
        <div className="sdm-available-views__details">
          <p className="sdm-available-views__details-purpose">
            {view.purpose.replace(/\.+$/, '')}
          </p>
          <p className="sdm-available-views__details-description">{view.description}</p>
          {legendItems.length > 0 ? (
            <div>
              <h4 className="sdm-available-views__details-heading">
                {thresholdHeading}
              </h4>
              <ul
                className="sdm-available-views__legend-list"
                aria-label={`${thresholdHeading} for ${view.name}`}
              >
                {legendItems.map((item) => (
                  <li
                    key={`${view.id}-legend-${item.color}-${item.label}`}
                    className="sdm-available-views__legend-item"
                  >
                    <span
                      className="sdm-available-views__legend-item-swatch"
                      style={{ backgroundColor: item.color }}
                      aria-hidden
                    />
                    <span className="sdm-available-views__legend-item-label">
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div>
            <h4 className="sdm-available-views__details-heading">Parameters</h4>
            <ul className="sdm-available-views__component-list">
              {view.componentLabels.map((label) => (
                <li key={`${view.id}-${label}`}>{label}</li>
              ))}
            </ul>
            {view.componentCount > 1 ? (
              <p className="sdm-available-views__details-description">
                See in Map applies every parameter in this view together.
              </p>
            ) : null}
          </div>
          {activationFailures.length > 0 ? (
            <div className="sdm-available-views__activation-status" role="status">
              <h4 className="sdm-available-views__details-heading">
                Activation notes
              </h4>
              <ul className="sdm-available-views__component-list">
                {activationFailures.map((failure) => (
                  <li key={`${failure.componentId}-${failure.reason}`}>
                    {failure.label}: {failureReasonLabel(failure.reason)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export default AvailableViewItem;
